import { Zap, MapPin, Phone, ShieldCheck, Truck, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import useCallTracking from "@/hooks/useCallTracking";
import { telHref, PHONE } from "@/constants/site";
import { useT } from "@/i18n/LanguageContext";

const ICONS = [Zap, Clock, Truck, MapPin, ShieldCheck];

export default function PromiseBar() {
  const trackCall = useCallTracking();
  const { t } = useT();
  const promises = t.promise.items.map((p, i) => ({ ...p, icon: ICONS[i] }));

  return (
    <section
      id="why"
      data-testid="promise-bar"
      className="relative bg-[#0A0A0A] text-white py-14 lg:py-16 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#1E63FF]/25 blur-[100px]" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <Reveal direction="left">
            <div className="overline text-[#1E63FF]">{t.promise.overline}</div>
            <h2 className="font-display font-semibold text-2xl sm:text-4xl tracking-tight mt-3 leading-[1.05]">
              {t.promise.heading1}<br className="hidden sm:inline" />
              <span className="text-white/60"> {t.promise.heading2}</span>
            </h2>
          </Reveal>

          <Reveal direction="right" delay={120}>
            <a
              href={telHref}
              onClick={trackCall("promise-bar")}
              className="btn-blue h-12 px-6 rounded-full inline-flex items-center gap-2.5 text-sm font-semibold pulse-blue"
            >
              <Phone className="w-4 h-4" />
              {t.promise.cta} {PHONE}
            </a>
          </Reveal>
        </div>

        <ul className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 lg:gap-6">
          {promises.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal as="li" key={i} delay={i * 80}>
                <div className="group h-full rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#1E63FF]/50 p-5 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#1E63FF]/15 text-[#1E63FF] flex items-center justify-center group-hover:bg-[#1E63FF] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.7} />
                  </div>
                  <div className="font-display font-semibold text-base sm:text-xl tracking-tight text-white mt-4 leading-tight">
                    {p.headline}
                  </div>
                  <div className="text-[12px] text-white/60 mt-1 uppercase tracking-wider-2">
                    {p.sub}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
